/**
 * Centralized logging utility for the application
 * 
 * Debug logs are disabled by default and only enabled when:
 * - URL contains ?appDebug=true query parameter
 * - Additional flags can be added: ?appDebug=true&debugStorage=true&debugAuth=true
 * 
 * This is scalable - add new debug categories as needed.
 */

// Parse URL parameters once at initialization
const getDebugFlags = (): Record<string, boolean> => {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  return {
    // Master debug flag - enables all debug logs
    appDebug: params.get('appDebug') === 'true',
    // Category-specific flags for fine-grained control
    debugStorage: params.get('debugStorage') === 'true',
    debugAuth: params.get('debugAuth') === 'true',
    debugCanvas: params.get('debugCanvas') === 'true',
    debugThumbnail: params.get('debugThumbnail') === 'true',
    debugSync: params.get('debugSync') === 'true',
    debugMedia: params.get('debugMedia') === 'true',
  };
};

// Cache the flags to avoid re-parsing on every log call
const debugFlags = getDebugFlags();

// Detect production environment — suppress non-error logs
const isDev = typeof process !== 'undefined'
  ? (typeof process.env === 'string' ? false : (process.env as any)?.NODE_ENV !== 'production')
  : (typeof window !== 'undefined' && window.location?.hostname === 'localhost');

/**
 * Check if debug logging is enabled (master flag or specific category)
 */
export const isDebugEnabled = (category?: string): boolean => {
  // Master flag enables all
  if (debugFlags.appDebug) return true;
  // Check specific category flag
  if (category && debugFlags[`debug${category}`]) return true;
  return false;
};

export const logger = {
  /**
   * Debug logs - only shown when appDebug=true or specific category flag is set
   * @param category Optional category for fine-grained control (e.g., 'Storage', 'Auth', 'Canvas')
   * @param args Log arguments
   */
  debug: (category: string, ...args: any[]) => {
    if (isDebugEnabled(category)) {
      console.log(`[${category}]`, ...args);
    }
  },

  /**
   * Info logs - only shown in development
   */
  info: (...args: any[]) => {
    if (isDev) console.info('[INFO]', ...args);
  },

  /**
   * Warning logs - only shown in development
   */
  warn: (...args: any[]) => {
    if (isDev) console.warn('[WARN]', ...args);
  },

  /**
   * Error logs - always shown
   */
  error: (...args: any[]) => {
    console.error('[ERROR]', ...args);
  },

  /**
   * Trace logs for performance debugging - only when appDebug=true
   */
  trace: (label: string, fn: () => any) => {
    if (isDebugEnabled()) {
      console.time(`[TRACE] ${label}`);
      const result = fn();
      console.timeEnd(`[TRACE] ${label}`);
      return result;
    }
    return fn();
  },

  /**
   * Async trace for promises - only when appDebug=true
   */
  traceAsync: async (label: string, fn: () => Promise<any>) => {
    if (isDebugEnabled()) {
      console.time(`[TRACE] ${label}`);
      const result = await fn();
      console.timeEnd(`[TRACE] ${label}`);
      return result;
    }
    return fn();
  },
};

/**
 * Convenience function for conditional debug logging without category
 * Use this for quick debug statements that should only show with appDebug=true
 */
export const debugLog = (...args: any[]) => {
  if (isDebugEnabled()) {
    console.log('[DEBUG]', ...args);
  }
};
